import classNames from 'classnames';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { DragType } from '../../../models/dragTypes';
import { addMultipleItemsAsync } from '../../../models/playlist/playlist';
import PlaylistItem from '../../../models/playlistItem/playlistItem';
import SoundCloudPlaylist from '../../../models/soundcloud/soundCloudPlaylist';
import PlaylistColumns, {
  ColumnKey,
  ColumnScope,
  TrackProperty,
} from '../../../services/columnSettings/playlistColumns';
import { Context } from '../../../services/_globalContext/context';
import { preventDefaultStopPropogation } from '../../../utilities/preventDefault';
import AddToPlaylistSubMenu from '../../common/addToPlaylist/addToPlaylistSubMenu';
import FadilaMenuItem from '../../common/contextMenu/fadilaMenuItem';
import useColumnSettings from '../../common/hooks/useColumnSettings';
import s from '../../common/itemRow/itemRow.module.scss';
import ItemRowWrapper from '../../common/itemRow/ItemRowWrapper';
import sS from '../../common/streamingServiceCommon/streamSvcSearchResultRow.module.scss';
import { openOnSoundCloud } from '../openOnSoundCloud';
import sc from '../soundCloud.module.scss';
import SoundCloudSyncSubMenu from '../soundCloudSyncSubMenu';

export interface SoundCloudPlaylistRowProps {
  /** Item array index */
  index: number;
  item: SoundCloudPlaylist;
  addPlaylistToPlaylist(
    addItems: (i: PlaylistItem[]) => void,
  ): (playlistUrn: string) => Promise<void>;
  openPlaylist(playlist: SoundCloudPlaylist): void;

  isSelected: boolean;
  selectedItemsRef: React.MutableRefObject<Iterable<SoundCloudPlaylist>>;

  handleToggleItem(plItm: SoundCloudPlaylist): void;
  changeItemSelectionState(
    plItm: SoundCloudPlaylist,
    selectState: boolean,
    removePrevious?: boolean,
  ): void;
  handleShiftSelect(plItm: SoundCloudPlaylist): void;
  clearSelected(): void;

  portalTarget?: HTMLElement;
}

/**
 * One playlist in the SoundCloud search results: open it, or add every
 * track in it to a library playlist by menu or by dragging it onto one.
 */
export default function PlaylistSearchResult({
  index,
  item,
  addPlaylistToPlaylist,
  openPlaylist,

  portalTarget,
  isSelected,
  selectedItemsRef,
  changeItemSelectionState,
  handleShiftSelect,
  handleToggleItem,
  clearSelected,
}: SoundCloudPlaylistRowProps) {
  const ctx = useContext(Context);

  const titleElRef = useRef<HTMLTableDataCellElement>(null);

  const [isDragging, setIsDragging] = useState(false);

  const { columns, columnSettings } = useColumnSettings(ColumnScope.SoundCloudPlaylistSearch);

  const onDragStart = (ev: React.DragEvent) => {
    console.debug('dragstart on playlist item');

    setIsDragging(true);

    // We set loads of data here since you can't call getData in onDragOver
    ev.dataTransfer.setData('dragType', DragType.SoundCloudPlaylist);
    // types.includes checks for matching drag type in onDragOver
    ev.dataTransfer.setData(DragType.SoundCloudPlaylist, DragType.SoundCloudPlaylist);
    ev.dataTransfer.setData('text/plain', item.urn);
    ev.dataTransfer.setData('name', item.title);

    ev.dataTransfer.effectAllowed = 'copy';

    if (titleElRef.current) {
      ev.dataTransfer.setDragImage(titleElRef.current, 0, 0);
    }
    if (!isSelected) {
      clearSelected();
      changeItemSelectionState(item, true, true);
    }

    // The drop handler reads the selection from here and fetches tracks
    // through the helper the page registered - see playlistRowCommon.ts.
    ctx.dndData[item.urn] = selectedItemsRef;
  };
  function onDragEnd(_ev: React.DragEvent) {
    setIsDragging(false);

    delete ctx.dndData[item.urn];
  }

  /** Add items in all selected playlists to target library playlist */
  const handleAddPlaylistsToPlaylist = useCallback(
    function handleAddPlaylistsToPlaylist(addItems: (i: PlaylistItem[]) => void) {
      const tasks = Array<Promise<any>>();
      const items = Array<PlaylistItem>();
      const addItemsToArray = (x: PlaylistItem[]) => items.push(...x);

      for (const pl of selectedItemsRef.current) {
        tasks.push(addPlaylistToPlaylist(addItemsToArray)(pl.urn));
      }

      addMultipleItemsAsync({ tasks, items, addItems });
    },
    [addPlaylistToPlaylist, selectedItemsRef],
  );

  return (
    <ItemRowWrapper<SoundCloudPlaylist>
      index={index}
      item={item}
      itemId={item.urn}
      className={sS.row}

      onDragStart={onDragStart}
      onDragEnd={onDragEnd}

      isSelected={isSelected}
      isDragging={isDragging}

      handleShiftSelect={() => handleShiftSelect(item)}
      handleToggleItem={() => handleToggleItem(item)}
      selectOnlyItem={() => changeItemSelectionState(item, true, true)}

      handlePlay={() => openPlaylist(item)}

      menuItems={
        <>
          <AddToPlaylistSubMenu
            newPlaylistName={item.title}
            playlistItemOrItems={(addItems) => handleAddPlaylistsToPlaylist(addItems)}
          />
          <SoundCloudSyncSubMenu playlistUrn={item.urn} title={item.title} />
          <FadilaMenuItem
            onClick={preventDefaultStopPropogation(() => openOnSoundCloud(item.permalink_url))}
          >
            Open on SoundCloud
          </FadilaMenuItem>
        </>
      }
      portalTarget={portalTarget}
    >
      {columnSettings && (
        <>
          <div className={classNames(s.rowCell, s.playBtn)}>
            <button className="play-button" onClick={() => openPlaylist(item)}>
              <span role="img" aria-label="Link">
                🔗
              </span>
            </button>
          </div>

          {columns.map((col) => {
            const colKey = col as ColumnKey;

            switch (colKey) {
              case TrackProperty.title:
                return (
                  <div
                    key={colKey}
                    ref={titleElRef}
                    className={classNames(s.rowCell, {
                      [s.rightAlignCell]: PlaylistColumns[colKey].alignRight,
                    })}
                    style={{
                      width: columnSettings[colKey]?.width,
                    }}
                    title={`${item.title} - ${item.user ? item.user.username : ''} (${item.track_count} tracks)`}
                  >
                    {item.title}
                    {item.user && <span className={sc.badge}>{item.user.username}</span>}
                  </div>
                );

              case 'image':
                return (
                  <div
                    key={colKey}
                    className={classNames(s.rowCell, {
                      [s.rightAlignCell]: PlaylistColumns[colKey].alignRight,
                    })}
                    style={{
                      width: columnSettings[colKey]?.width,
                    }}
                  >
                    {item.artwork_url && (
                      <img
                        className={sc.playlistImgLarge}
                        src={item.artwork_url}
                        alt={item.title}
                      />
                    )}
                  </div>
                );

              default:
                throw new Error('Unexpected column mismatch!');
            }
          })}
        </>
      )}
    </ItemRowWrapper>
  );
}
